import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Copy, DeviceMobile } from "@phosphor-icons/react";
import api from "../lib/api";
import { flag, fmtData, fmtDate, countryName } from "../lib/format";
import Header from "../components/Header";
import Footer from "../components/Footer";

export default function EsimDetails() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [esim, setEsim] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/me/esims/${id}`).then(({ data }) => setEsim(data)).catch(() => {});
  }, [id]);

  const copy = () => {
    navigator.clipboard.writeText(esim.qr_code_text || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!esim) return (
    <div className="min-h-screen"><Header /><div className="max-w-3xl mx-auto px-6 py-24 text-ink-500">{t("common.loading")}</div></div>
  );

  const pct = esim.data_total_mb > 0 ? (esim.data_used_mb / esim.data_total_mb) * 100 : 0;

  return (
    <div className="min-h-screen" data-testid="esim-details-page">
      <Header />
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-12">
        <Link to="/dashboard" className="btn-ghost text-sm !pl-2 mb-4 inline-flex"><ArrowLeft size={16} /> {t("common.back")}</Link>
        <div className="flex items-start gap-4 mb-10">
          <span className="text-5xl">{flag(esim.plan_snapshot.country_iso2)}</span>
          <div>
            <div className="overline">{t("esim.details")}</div>
            <h1 className="font-display font-bold text-3xl md:text-5xl tracking-tight">{esim.plan_snapshot.name}</h1>
            <div className="text-ink-700 mt-1">{countryName(esim.plan_snapshot.country_iso2, i18n.language, esim.plan_snapshot.country)}</div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          <div className="card-soft p-8 bg-gradient-to-br from-white to-cobalt-50">
            <div className="overline mb-4">{t("esim.installation")}</div>
            {esim.qr_image_base64 && (
              <div className="bg-white rounded-3xl p-6 w-fit shadow-soft">
                <img src={`data:image/png;base64,${esim.qr_image_base64}`} alt="QR" className="w-56 h-56" data-testid="esim-detail-qr" />
              </div>
            )}
            <div className="mt-6 flex flex-wrap gap-2">
              {esim.ios_install_link && (
                <a href={esim.ios_install_link} target="_blank" rel="noreferrer" className="btn-primary text-sm !py-2.5 !px-4">
                  <DeviceMobile size={16} weight="bold" /> {t("checkout.iosInstall")}
                </a>
              )}
              <button onClick={copy} className="btn-ghost text-sm !py-2.5 !px-4">
                <Copy size={14} /> {copied ? t("checkout.copied") : t("checkout.copyLPA")}
              </button>
            </div>
            <p className="mt-4 text-[11px] text-ink-400" data-testid="esim-detail-provider-note">
              {t("esim.providedBy", "eSIM connectivity provided by Yesim")}
            </p>
          </div>

          <div className="space-y-4">
            <div className="card-soft p-6">
              <div className="overline mb-3">{t("esim.usage")}</div>
              <div className="flex items-baseline justify-between mb-3">
                <div className="font-display font-bold text-4xl tracking-tight">{fmtData(esim.data_used_mb)}</div>
                <div className="text-ink-500">/ {fmtData(esim.data_total_mb)}</div>
              </div>
              <div className="h-2.5 bg-ink-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cobalt-500 to-cobalt-400 rounded-full" style={{ width: `${pct}%` }} />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <div className="bg-cobalt-50 rounded-2xl p-3">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-cobalt-600">{t("dash.dataLeft")}</div>
                  <div className="font-display font-bold mt-0.5">{fmtData(esim.data_left_mb)}</div>
                </div>
                <div className="bg-coral-soft/60 rounded-2xl p-3">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-coral-deep">{t("dash.expires")}</div>
                  <div className="font-display font-bold mt-0.5">{fmtDate(esim.expiry_date)}</div>
                </div>
              </div>
            </div>

            <div className="card-soft p-6 space-y-3 text-sm">
              <div><div className="overline mb-1">{t("checkout.iccid")}</div><div className="font-mono text-xs break-all text-ink-700">{esim.iccid}</div></div>
              <div><div className="overline mb-1">{t("common.status")}</div><div className="font-semibold">{esim.status}</div></div>
              {esim.activation_date && (<div><div className="overline mb-1">Activated</div><div>{fmtDate(esim.activation_date)}</div></div>)}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
